import axios from 'axios';

// Use relative URLs - frontend and backend share the same origin via ingress
const API = '/api';

// In-memory token storage (fallback when httpOnly cookies don't work in iframes)
let memoryToken = null;

export const setMemoryToken = (token) => { memoryToken = token; };
export const clearMemoryToken = () => { memoryToken = null; };

// Create axios instance with httpOnly cookie support
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor - adds Authorization header from memory token as fallback
apiClient.interceptors.request.use((config) => {
  if (memoryToken) {
    config.headers.Authorization = `Bearer ${memoryToken}`;
  }
  return config;
});

// Response interceptor - handles token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearMemoryToken();
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'token_expired' } }));
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username, password) => {
  const response = await axios.post(`${API}/auth/login`, { username, password });
  return response.data;
};

export const getMe = async () => {
  const response = await apiClient.get('/auth/me');
  return response.data;
};

export const registerUser = async (userData) => {
  const response = await apiClient.post('/auth/register', userData);
  return response.data;
};

// Stores
export const getStores = async () => {
  const response = await apiClient.get('/stores');
  return response.data;
};

export const createStore = async (storeData) => {
  const response = await apiClient.post('/stores', storeData);
  return response.data;
};

export const updateStore = async (storeId, storeData) => {
  const response = await apiClient.put(`/stores/${storeId}`, storeData);
  return response.data;
};

// Sales
export const createSale = async (saleData) => {
  const response = await apiClient.post('/sales', saleData);
  return response.data;
};

export const getSales = async (params = {}) => {
  const response = await apiClient.get('/sales', { params });
  return response.data;
};

export const getSalesWithBalance = async (params = {}) => {
  const response = await apiClient.get('/sales/with-balance', { params });
  return response.data;
};

// Expenses
export const createExpense = async (expenseData) => {
  const response = await apiClient.post('/expenses', expenseData);
  return response.data;
};

export const getExpenses = async (params = {}) => {
  const response = await apiClient.get('/expenses', { params });
  return response.data;
};

export const getExpensesWithBalance = async (params = {}) => {
  const response = await apiClient.get('/expenses/with-balance', { params });
  return response.data;
};

// Cashbook
export const getCashbook = async (params = {}) => {
  const response = await apiClient.get('/cashbook', { params });
  return response.data;
};

export const editExpense = async (expenseId, editData) => {
  const response = await apiClient.put(`/expenses/${expenseId}`, editData);
  return response.data;
};

// Petty Cash
export const createPettyCashCount = async (countData) => {
  const response = await apiClient.post('/petty-cash/count', countData);
  return response.data;
};

export const usePettyCash = async (useData) => {
  const response = await apiClient.post('/petty-cash/use', useData);
  return response.data;
};

export const getPettyCashHistory = async (params = {}) => {
  const response = await apiClient.get('/petty-cash/history', { params });
  return response.data;
};

export const createPettyCashRequest = async (data) => {
  const response = await apiClient.post('/petty-cash/request', data);
  return response.data;
};

export const getPettyCashRequests = async (params = {}) => {
  const response = await apiClient.get('/petty-cash/requests', { params });
  return response.data;
};

export const confirmPettyCashRequest = async (requestId) => {
  const response = await apiClient.put(`/petty-cash/requests/${requestId}/confirm`);
  return response.data;
};

export const completePettyCashRequest = async (requestId) => {
  const response = await apiClient.put(`/petty-cash/requests/${requestId}/complete`);
  return response.data;
};

export const cancelPettyCashRequest = async (requestId) => {
  const response = await apiClient.put(`/petty-cash/requests/${requestId}/cancel`);
  return response.data;
};

export const transferPettyCashToCashFlow = async (data) => {
  const response = await apiClient.post('/petty-cash/transfer-to-cash', data);
  return response.data;
};

export const refundPettyCash = async (data) => {
  const response = await apiClient.post('/petty-cash/refund', data);
  return response.data;
};

// Authorization Codes
export const generateAuthCode = async (shiftNumber) => {
  const response = await apiClient.post(`/authorization/generate?shift_number=${shiftNumber}`);
  return response.data;
};

export const getCurrentAuthCode = async () => {
  const response = await apiClient.get('/authorization/current');
  return response.data;
};

// Suppliers
export const getSuppliers = async (params = {}) => {
  const response = await apiClient.get('/suppliers', { params });
  return response.data;
};

export const getAllSuppliers = async () => {
  const response = await apiClient.get('/suppliers/all');
  return response.data;
};

export const createSupplier = async (supplierData) => {
  const response = await apiClient.post('/suppliers', supplierData);
  return response.data;
};

export const updateSupplier = async (supplierId, supplierData) => {
  const response = await apiClient.put(`/suppliers/${supplierId}`, supplierData);
  return response.data;
};

export const deleteSupplier = async (supplierId) => {
  const response = await apiClient.delete(`/suppliers/${supplierId}`);
  return response.data;
};

// Dashboard
export const getDashboard = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.store_id) queryParams.append('store_id', params.store_id);
  if (params.year) queryParams.append('year', params.year);
  if (params.month) queryParams.append('month', params.month);
  
  let url = '/dashboard';
  if (queryParams.toString()) url += `?${queryParams.toString()}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

export const getStoreComparison = async (period = 'today') => {
  const response = await apiClient.get(`/dashboard/store-comparison?period=${period}`);
  return response.data;
};

export const getSalesTrends = async (period = 'weekly') => {
  const response = await apiClient.get(`/dashboard/trends?period=${period}`);
  return response.data;
};

// Reports
export const generateShiftReport = async (shiftNumber, shiftDate, storeId = null) => {
  let url = `/reports/shift?shift_number=${shiftNumber}&shift_date=${shiftDate}`;
  if (storeId) url += `&store_id=${storeId}`;
  const response = await apiClient.post(url);
  return response.data;
};

export const getShiftReports = async (storeId = null) => {
  const params = {};
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/reports/shifts', { params });
  return response.data;
};

// Annual Reports
export const getExpensesByCategoryReport = async (params = {}) => {
  const response = await apiClient.get('/reports/expenses-by-category', { params });
  return response.data;
};

export const getExpensesBySupplierReport = async (params = {}) => {
  const response = await apiClient.get('/reports/expenses-by-supplier', { params });
  return response.data;
};

// Audit Logs
export const getAuditLogs = async (params = {}) => {
  const response = await apiClient.get('/audit-logs', { params });
  return response.data;
};

// Notifications
export const getNotifications = async () => {
  const response = await apiClient.get('/notifications');
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await apiClient.put(`/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await apiClient.put('/notifications/mark-all-read');
  return response.data;
};

// Expense Categories
export const getExpenseCategories = async () => {
  const response = await apiClient.get('/expense-categories');
  return response.data;
};

export const createExpenseCategory = async (categoryData) => {
  const response = await apiClient.post('/expense-categories', categoryData);
  return response.data;
};

export const deleteExpenseCategory = async (categoryId) => {
  const response = await apiClient.delete(`/expense-categories/${categoryId}`);
  return response.data;
};

// Users Management
export const getUsers = async () => {
  const response = await apiClient.get('/users');
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await apiClient.put(`/users/${userId}`, userData);
  return response.data;
};

export const toggleUserActive = async (userId) => {
  const response = await apiClient.put(`/users/${userId}/toggle-active`);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await apiClient.delete(`/users/${userId}`);
  return response.data;
};

// Device Management
export const getPendingDevices = async () => {
  const response = await apiClient.get('/devices/pending');
  return response.data;
};

export const getAuthorizedDevices = async () => {
  const response = await apiClient.get('/devices/authorized');
  return response.data;
};

export const getBlockedDevices = async () => {
  const response = await apiClient.get('/devices/blocked');
  return response.data;
};

export const authorizeDevice = async (deviceId) => {
  const response = await apiClient.put(`/devices/${deviceId}/authorize`);
  return response.data;
};

export const revokeDevice = async (deviceId) => {
  const response = await apiClient.put(`/devices/${deviceId}/revoke`);
  return response.data;
};

export const blockDevice = async (deviceId) => {
  const response = await apiClient.put(`/devices/${deviceId}/block`);
  return response.data;
};

export const unblockDevice = async (deviceId) => {
  const response = await apiClient.put(`/devices/${deviceId}/unblock`);
  return response.data;
};

export const deleteDevice = async (deviceId) => {
  const response = await apiClient.delete(`/devices/${deviceId}`);
  return response.data;
};

export const deleteDevicesByStore = async (storeId) => {
  const response = await apiClient.delete(`/devices/store/${storeId}`);
  return response.data;
};

export const deleteAllDevicesForUser = async (userId) => {
  const response = await apiClient.delete(`/devices/user/${userId}/all`);
  return response.data;
};

export const deleteDevicesNoStore = async () => {
  const response = await apiClient.delete('/devices/no-store');
  return response.data;
};

// Sales Report by Payment Method
export const getSalesByPaymentMethod = async (storeId = null, year = null, month = null) => {
  let url = '/reports/sales-by-payment-method';
  const params = new URLSearchParams();
  if (storeId) params.append("store_id", storeId);
  if (year) params.append("year", year);
  if (month) params.append("month", month);
  if (params.toString()) url += `?${params.toString()}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

// Delete Sale (Admin only)
export const deleteSale = async (saleId) => {
  const response = await apiClient.delete(`/sales/${saleId}`);
  return response.data;
};

// Delete Expense (Admin only)
export const deleteExpense = async (expenseId) => {
  const response = await apiClient.delete(`/expenses/${expenseId}`);
  return response.data;
};

// Store Summary Report
export const getStoreSummary = async (year = null, month = null) => {
  let url = '/reports/store-summary';
  const params = new URLSearchParams();
  if (year) params.append("year", year);
  if (month) params.append("month", month);
  if (params.toString()) url += `?${params.toString()}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

// Update user permissions
export const updateUserPermissions = async (userId, permissions) => {
  const response = await apiClient.put(`/users/${userId}`, { permissions });
  return response.data;
};

// POS Commissions Report
export const getPosCommissionsReport = async (storeId = null, year = null, month = null) => {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId);
  if (year) params.append('year', year);
  if (month) params.append('month', month);
  const response = await apiClient.get(`/reports/pos-commissions?${params}`);
  return response.data;
};

// Monthly Sales by Store Report
export const getMonthlySalesByStore = async (year = null) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  const response = await apiClient.get(`/reports/monthly-sales-by-store?${params}`);
  return response.data;
};

// Bank Commissions Configuration
export const getBankCommissions = async () => {
  const response = await apiClient.get('/bank-commissions');
  return response.data;
};

export const setBankCommission = async (storeId, bankName, commissionRate) => {
  const response = await apiClient.post('/bank-commissions', {
    store_id: storeId,
    bank_name: bankName,
    commission_rate: commissionRate
  });
  return response.data;
};

// Admin: Recalcular balances de todas las tiendas
export const recalculateAllBalances = async () => {
  const response = await apiClient.post('/admin/recalculate-balances');
  return response.data;
};

// Admin: Recalcular balance de una tienda específica
export const recalculateSingleBalance = async (storeId) => {
  const response = await apiClient.post(`/admin/recalculate-balance/${storeId}`);
  return response.data;
};

// Admin: Quitar tienda asignada a un usuario
export const removeUserStore = async (userId) => {
  const response = await apiClient.put(`/users/${userId}`, { store_id: 'REMOVE' });
  return response.data;
};

// Admin: Quitar tiendas de TODOS los usuarios administrativos
export const fixAdminStores = async () => {
  const response = await apiClient.post('/admin/fix-admin-stores');
  return response.data;
};

// Admin: Resetear tienda
export const resetStore = async (storeId, confirm = false) => {
  const url = confirm 
    ? `/admin/reset-store/${storeId}?confirm=true`
    : `/admin/reset-store/${storeId}`;
  const response = await apiClient.post(url);
  return response.data;
};

// Admin: Eliminar tienda completamente
export const deleteStore = async (storeId, confirm = false) => {
  const url = confirm 
    ? `/admin/delete-store/${storeId}?confirm=true`
    : `/admin/delete-store/${storeId}`;
  const response = await apiClient.delete(url);
  return response.data;
};

// Admin: Auditar transacciones de una tienda
export const auditStoreTransactions = async (storeId) => {
  const response = await apiClient.get(`/admin/audit-transactions/${storeId}`);
  return response.data;
};

// Export apiClient for direct use in components
export { apiClient };
